/**
 * Per-eval node-level surface — `/evals/:evalId/nodes`.
 *
 * Focused page for everything node-level. Two things live here:
 *   1. Authoring per-node expected outputs (workflow-scoped, but
 *      reached via the eval since that's where users land).
 *   2. Viewing this eval's most recent per-node scoring breakdown.
 *
 * Workflow-level scoring, attached judges, per-entry runs, deterministic
 * legacy metrics are all on the sibling `/evals/:evalId` page. This page
 * deliberately stays clean — node thinking only.
 *
 * Scoring is deep-equality (eval-harness `scoreNodes`). No graders here.
 */
import { useClient } from '@cortex/react-query';
import type { Evaluation, GroundTruth, Workflow, WorkflowVersion } from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { EmptyState } from '../../../components/common/EmptyState';
import { InfoHint } from '../../../components/common/InfoHint';
import { JsonViewer } from '../../../components/common/JsonViewer';
import { Spinner } from '../../../components/common/Spinner';
import { EvalDetailShell } from '../../../components/evals/EvalDetailShell';
import { tdStyle } from '../../../components/evals/eval-detail-shared';
import type { IPerEntryResult } from '../../../components/evals/eval-detail-shared';
import { routes } from '../../../lib/constants';
import { formatDateTime } from '../../../lib/format';

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

export const Route = createFileRoute('/evals/$evalId/nodes')({ component: EvalNodesPage });

function EvalNodesPage() {
  const { evalId } = Route.useParams();
  return (
    <EvalDetailShell evalId={evalId}>
      {({ evaluation }) => <Body evaluation={evaluation} />}
    </EvalDetailShell>
  );
}

function Body({ evaluation }: { evaluation: Evaluation }) {
  const client = useClient();
  const qc = useQueryClient();
  // KG evals have no carrier workflow () — node-level
  // scoring is a workflow-only concept.
  const kgEarlyReturn = evaluation.workflowId == null;
  const workflowId = evaluation.workflowId ?? '';

  const workflow = useQuery({
    enabled: !kgEarlyReturn,
    queryKey: ['workflow', workflowId],
    queryFn: () => client.http.get(`v1/workflows/${workflowId}`).json<Workflow>(),
  });
  const versions = useQuery({
    enabled: !kgEarlyReturn,
    queryKey: ['workflow-versions', workflowId],
    queryFn: () => client.http.get(`v1/workflows/${workflowId}/versions`).json<WorkflowVersion[]>(),
  });
  const gts = useQuery({
    enabled: !kgEarlyReturn,
    queryKey: ['ground-truth', workflowId, 'node-only'],
    queryFn: () =>
      client.http
        .get(`v1/ground-truth?workflowId=${encodeURIComponent(workflowId)}&limit=500`)
        .json<IListEnvelope<GroundTruth>>(),
  });

  // Historical node-level scoring across every Run this eval ever fired.
  // Joined server-side from NodeExecution + GroundTruth (no derived state
  // stored), returned chronological so we can plot per-node trend strips.
  const history = useQuery({
    enabled: !kgEarlyReturn,
    queryKey: ['eval-node-history', evaluation.id],
    queryFn: () =>
      client.http.get(`v1/evaluations/${evaluation.id}/node-scoring-history`).json<{
        evaluationId: string;
        totalRuns: number;
        samples: Array<{
          runId: string;
          runStartedAt: string;
          runStatus: string;
          nodeKey: string;
          groundTruthId: string;
          groundTruthName: string | null;
          passed: boolean;
          reason: string | null;
        }>;
      }>(),
  });

  const { nodes, outputSchemaByNodeKey } = useMemo(() => {
    const latest = versions.data?.[0];
    const graph = latest?.graph as
      | { nodes?: Array<{ nodeKey: string; nodeType: string; outputSchema?: unknown }> }
      | undefined;
    const ns = graph?.nodes ?? [];
    const map = new Map<string, unknown>();
    for (const n of ns) if (n.outputSchema) map.set(n.nodeKey, n.outputSchema);
    return { nodes: ns, outputSchemaByNodeKey: map };
  }, [versions.data]);

  const gtByNode = useMemo(() => {
    const all = gts.data?.items ?? [];
    const byNode = new Map<string, GroundTruth[]>();
    for (const e of all) {
      if (!e.nodeId) continue;
      const list = byNode.get(e.nodeId) ?? [];
      list.push(e);
      byNode.set(e.nodeId, list);
    }
    return byNode;
  }, [gts.data]);

  // This eval's latest scoring — flatten nodeResults per node for the
  // pass-rate badge. Reads scoreSummary.perEntry[*].nodeResults.
  const scoreByNode = useMemo(() => {
    const summary = (evaluation.scoreSummary as Record<string, unknown> | null) ?? null;
    const perEntry: IPerEntryResult[] = Array.isArray(summary?.perEntry)
      ? (summary?.perEntry as IPerEntryResult[])
      : [];
    type Sample = {
      passed: boolean;
      reason?: string;
      entryName: string | null;
      runId: string | null;
    };
    const byNode = new Map<string, { passed: number; failed: number; samples: Sample[] }>();
    for (const e of perEntry) {
      for (const r of e.nodeResults ?? []) {
        const b = byNode.get(r.nodeId) ?? { passed: 0, failed: 0, samples: [] };
        if (r.passed) b.passed += 1;
        else b.failed += 1;
        b.samples.push({
          passed: r.passed,
          reason: r.reason,
          entryName: e.entryName ?? null,
          runId: e.runId ?? null,
        });
        byNode.set(r.nodeId, b);
      }
    }
    return byNode;
  }, [evaluation.scoreSummary]);

  // Group history samples by nodeKey so each NodeSection only sees its own
  // strip. Within each node, preserves chronological order from the server.
  const historyByNode = useMemo(() => {
    const byNode = new Map<
      string,
      Array<{
        runId: string;
        runStartedAt: string;
        runStatus: string;
        passed: boolean;
        reason: string | null;
        groundTruthName: string | null;
      }>
    >();
    for (const s of history.data?.samples ?? []) {
      const list = byNode.get(s.nodeKey) ?? [];
      list.push({
        runId: s.runId,
        runStartedAt: s.runStartedAt,
        runStatus: s.runStatus,
        passed: s.passed,
        reason: s.reason,
        groundTruthName: s.groundTruthName,
      });
      byNode.set(s.nodeKey, list);
    }
    return byNode;
  }, [history.data]);

  const onMutated = () =>
    qc.invalidateQueries({ queryKey: ['ground-truth', workflowId, 'node-only'] });

  if (kgEarlyReturn) {
    return (
      <div className="docset-panel" style={{ padding: 'var(--sp-4)', textAlign: 'center' }}>
        <div className="t-sm">
          Node-level scoring doesn&apos;t apply to KG evals — this view is workflow-only.
        </div>
      </div>
    );
  }

  if (workflow.isLoading || versions.isLoading || gts.isLoading) return <Spinner />;
  if (nodes.length === 0) {
    return (
      <EmptyState
        title="No graph yet"
        description="This eval's workflow has no committed nodes to score. Build the graph first."
      />
    );
  }

  // Aggregate totals across every node — drives the hero card. Includes
  // nodes that have zero GTs so the page tells a complete story about
  // the workflow's node-level coverage, not just where work has happened.
  const totalEntries = nodes.reduce((a, n) => a + (gtByNode.get(n.nodeKey)?.length ?? 0), 0);
  const totalChecks = nodes.reduce((a, n) => {
    const s = scoreByNode.get(n.nodeKey);
    return a + (s ? s.passed + s.failed : 0);
  }, 0);
  const totalPassed = nodes.reduce((a, n) => a + (scoreByNode.get(n.nodeKey)?.passed ?? 0), 0);
  const passRate = totalChecks > 0 ? totalPassed / totalChecks : 0;
  const nodesWithGT = nodes.filter((n) => (gtByNode.get(n.nodeKey)?.length ?? 0) > 0).length;
  const coverage = nodes.length > 0 ? nodesWithGT / nodes.length : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      {/* Hero: big number, secondary stats, back-link. Always renders, even
          when the workflow has zero GTs — emptiness is the headline then. */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--sp-3)',
          alignItems: 'stretch',
          padding: 'var(--sp-3)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-md)',
          background: 'linear-gradient(135deg, var(--surface-1) 0%, var(--surface-2) 100%)',
        }}
      >
        <div style={{ flex: '0 0 220px' }}>
          <div
            className="t-xs t-muted"
            style={{ textTransform: 'uppercase', letterSpacing: '.06em' }}
          >
            Node-level pass rate
          </div>
          <div
            style={{
              fontSize: 56,
              fontWeight: 'var(--fw-med)',
              lineHeight: 1,
              marginTop: 4,
              color:
                totalChecks === 0
                  ? 'var(--text-secondary)'
                  : passRate === 1
                    ? 'var(--status-success, #2eb872)'
                    : passRate >= 0.7
                      ? 'var(--horizon-glow)'
                      : 'var(--status-danger, #e44545)',
            }}
          >
            {totalChecks === 0 ? '—' : `${(passRate * 100).toFixed(0)}%`}
          </div>
          <div className="t-xs t-muted" style={{ marginTop: 2 }}>
            {totalChecks === 0
              ? 'Run the eval to see scoring'
              : `${totalPassed} / ${totalChecks} checks passed`}
          </div>
        </div>

        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>Node-level evals</h2>
            <p className="t-sm t-muted" style={{ margin: '4px 0 0 0' }}>
              Per-node expected outputs for{' '}
              <Link to={routes.workflowDetail(workflowId)}>
                {workflow.data?.name ?? 'workflow'}
              </Link>
              . Scored deep-equal against each Run&apos;s actual node output.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 'var(--sp-3)', marginTop: 'var(--sp-2)' }}>
            <Stat label="Workflow nodes" value={nodes.length} />
            <Stat
              label="With GT"
              value={`${nodesWithGT} / ${nodes.length}`}
              accent={
                coverage === 1
                  ? 'var(--status-success, #2eb872)'
                  : coverage === 0
                    ? 'var(--status-danger, #e44545)'
                    : 'var(--horizon-glow)'
              }
            />
            <Stat label="GT entries" value={totalEntries} />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          <Link to={routes.evalDetail(evaluation.id)} className="btn btn--ghost btn--sm">
            ← Workflow-level
          </Link>
        </div>
      </div>

      {totalEntries === 0 ? (
        <div
          style={{
            padding: 'var(--sp-3)',
            textAlign: 'center',
            border: '2px dashed var(--border-subtle)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--text-secondary)',
          }}
        >
          <p style={{ marginBottom: 6, fontSize: 14 }}>
            No node-level ground truth yet. Pick a node below and click <strong>+ Add entry</strong>
            .
          </p>
          <p className="t-xs t-muted" style={{ margin: 0 }}>
            One entry per scenario you want to lock down — the runner deep-equal-checks each
            node&apos;s actual output against your expected output every time this eval runs.
          </p>
        </div>
      ) : null}

      {nodes.map((n) => (
        <NodeSection
          key={n.nodeKey}
          workflowId={workflowId}
          nodeKey={n.nodeKey}
          nodeType={n.nodeType}
          outputSchema={outputSchemaByNodeKey.get(n.nodeKey) ?? null}
          entries={gtByNode.get(n.nodeKey) ?? []}
          score={scoreByNode.get(n.nodeKey) ?? null}
          history={historyByNode.get(n.nodeKey) ?? []}
          onMutated={onMutated}
        />
      ))}
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: string;
}) {
  return (
    <div>
      <div className="t-xs t-muted" style={{ textTransform: 'uppercase', letterSpacing: '.04em' }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 'var(--fw-med)', color: accent }}>{value}</div>
    </div>
  );
}

function NodeSection({
  workflowId,
  nodeKey,
  nodeType,
  outputSchema,
  entries,
  score,
  history,
  onMutated,
}: {
  workflowId: string;
  nodeKey: string;
  nodeType: string;
  outputSchema: unknown | null;
  entries: GroundTruth[];
  score: {
    passed: number;
    failed: number;
    samples: Array<{
      passed: boolean;
      reason?: string;
      entryName: string | null;
      runId: string | null;
    }>;
  } | null;
  history: Array<{
    runId: string;
    runStartedAt: string;
    runStatus: string;
    passed: boolean;
    reason: string | null;
    groundTruthName: string | null;
  }>;
  onMutated: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const denom = score ? score.passed + score.failed : 0;
  const rate = score && denom > 0 ? score.passed / denom : 0;
  const allPassed = score ? score.failed === 0 : false;
  const hasEntries = entries.length > 0;
  return (
    <section
      style={{
        border: '1px solid var(--border-subtle)',
        borderLeft: score
          ? `4px solid ${allPassed ? 'var(--status-success, #2eb872)' : 'var(--status-danger, #e44545)'}`
          : '4px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--sp-3)',
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 'var(--sp-3)',
          marginBottom: 'var(--sp-3)',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-2)' }}>
            <h2 style={{ margin: 0, fontSize: 22 }}>
              <code className="t-mono">{nodeKey}</code>
            </h2>
            <span className="fk-badge">{nodeType}</span>
          </div>
          <div className="t-xs t-muted" style={{ marginTop: 2 }}>
            {entries.length} ground-truth {entries.length === 1 ? 'entry' : 'entries'}
          </div>
        </div>

        {/* Big right-aligned pass-rate display per node. When the eval has
            run, show percentage + pass/total. When no run yet but GTs
            exist, show "pending". When no GTs, show "—". */}
        <div style={{ textAlign: 'right' }}>
          <div
            style={{
              fontSize: 36,
              fontWeight: 'var(--fw-med)',
              lineHeight: 1,
              color: !score
                ? 'var(--text-secondary)'
                : allPassed
                  ? 'var(--status-success, #2eb872)'
                  : rate >= 0.5
                    ? 'var(--horizon-glow)'
                    : 'var(--status-danger, #e44545)',
            }}
          >
            {score ? `${(rate * 100).toFixed(0)}%` : hasEntries ? '—' : '—'}
          </div>
          <div className="t-xs t-muted">
            {score
              ? `${score.passed} / ${denom} latest`
              : hasEntries
                ? 'awaiting next run'
                : 'no entries'}
          </div>
        </div>

        <button
          type="button"
          className={`btn btn--${hasEntries ? 'ghost' : 'primary'} btn--sm`}
          onClick={() => setAdding((v) => !v)}
        >
          {adding ? 'Cancel' : hasEntries ? '+ Add entry' : '+ Add first entry'}
        </button>
      </header>

      {/* Score-bar — visible even when 0 entries, just empty grey. Adds
          visual weight to the card so nodes with no GT don't look like
          forgotten leftovers. */}
      <div
        style={{
          display: 'flex',
          width: '100%',
          height: 10,
          borderRadius: 5,
          overflow: 'hidden',
          background: 'var(--surface-2)',
          marginBottom: 'var(--sp-3)',
        }}
      >
        {score && score.passed > 0 ? (
          <div style={{ flex: score.passed, background: 'var(--status-success, #2eb872)' }} />
        ) : null}
        {score && score.failed > 0 ? (
          <div style={{ flex: score.failed, background: 'var(--status-danger, #e44545)' }} />
        ) : null}
      </div>

      {/*
        Historical trend strip — one segment per (Run × node-GT) ever scored.
        Oldest left, newest right; hover for run id + timestamp + GT name.
        Reconstructed at read time from NodeExecution + GroundTruth so no
        derived state has to be persisted. Hidden when this node has no
        history at all (workflows that never ran with node-GT in place).
      */}
      {history.length > 0 ? (
        <div style={{ marginBottom: 'var(--sp-3)' }}>
          <div
            className="t-xs t-muted"
            style={{
              textTransform: 'uppercase',
              letterSpacing: '.04em',
              marginBottom: 4,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <span>Trend ({history.length} historical checks)</span>
            <span>
              {history.filter((h) => h.passed).length} pass ·{' '}
              {history.filter((h) => !h.passed).length} fail
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 2,
              height: 14,
              borderRadius: 3,
              overflow: 'hidden',
              background: 'var(--surface-2)',
              padding: 1,
            }}
            title={`Oldest left → newest right (${history.length} samples)`}
          >
            {history.map((h, i) => (
              <div
                key={`${h.runId}-${i}`}
                style={{
                  flex: 1,
                  background: h.passed
                    ? 'var(--status-success, #2eb872)'
                    : 'var(--status-danger, #e44545)',
                  borderRadius: 1,
                }}
                title={`${h.passed ? '✓ pass' : '✗ fail'} · ${h.groundTruthName ?? '(unnamed)'} · ${new Date(h.runStartedAt).toLocaleString()}${h.reason ? `\n${h.reason}` : ''}`}
              />
            ))}
          </div>
        </div>
      ) : null}

      {/* Schema reference — always visible. Helps authors picture what
          they're scoring against without an extra click. */}
      {outputSchema ? (
        <div style={{ marginBottom: 'var(--sp-3)' }}>
          <div
            className="t-xs t-muted"
            style={{ textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4 }}
          >
            Output schema reference
            <InfoHint text="The shape the runner expects this node to produce. Your expectedOutput should match — the eval-harness compares them with deep-equality." />
          </div>
          <pre
            className="t-xs t-mono"
            style={{
              background: 'var(--surface-2)',
              padding: 'var(--sp-2)',
              borderRadius: 4,
              margin: 0,
              maxHeight: 160,
              overflow: 'auto',
            }}
          >
            {JSON.stringify(outputSchema, null, 2)}
          </pre>
        </div>
      ) : null}

      {adding ? (
        <AddEntryForm
          workflowId={workflowId}
          nodeKey={nodeKey}
          outputSchema={outputSchema}
          onCancel={() => setAdding(false)}
          onCreated={() => {
            setAdding(false);
            onMutated();
          }}
        />
      ) : null}

      {/* Latest run breakdown — only renders when this eval has scored
          this node. Mirrors the per-entry table on the main eval page:
          chevron expand, fk-badge--completed/failed, full run trace
          link on each sample. */}
      {score && score.samples.length > 0 ? <ScoreBreakdown samples={score.samples} /> : null}

      {entries.length === 0 && !adding ? (
        <p className="t-xs t-muted" style={{ margin: 0 }}>
          No ground truth entries for this node yet.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          {entries.map((e) => (
            <EntryRow key={e.id} entry={e} onDeleted={onMutated} />
          ))}
        </div>
      )}
    </section>
  );
}

function ScoreBreakdown({
  samples,
}: {
  samples: Array<{
    passed: boolean;
    reason?: string;
    entryName: string | null;
    runId: string | null;
  }>;
}) {
  const [open, setOpen] = useState(false);
  const passed = samples.filter((s) => s.passed).length;
  const failed = samples.length - passed;
  const allPassed = failed === 0;
  return (
    <table
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: 13,
        marginBottom: 'var(--sp-2)',
      }}
    >
      <tbody>
        <tr
          onClick={() => setOpen((v) => !v)}
          style={{
            borderTop: '1px solid var(--border-subtle, rgba(255,255,255,0.08))',
            cursor: 'pointer',
          }}
        >
          <td style={{ ...tdStyle, width: 24 }}>
            <span style={{ display: 'inline-block', width: 12 }}>{open ? '▾' : '▸'}</span>
          </td>
          <td style={tdStyle}>Latest run results</td>
          <td style={tdStyle}>
            <span className={`fk-badge--${allPassed ? 'completed' : 'failed'}`}>
              {passed} / {samples.length}
            </span>
          </td>
        </tr>
        {open ? (
          <tr style={{ background: 'var(--surface-2)' }}>
            <td colSpan={3} style={{ padding: 'var(--sp-3)' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
                {samples.map((s, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 'var(--sp-2)',
                      paddingBottom: 'var(--sp-1)',
                      borderBottom:
                        i < samples.length - 1
                          ? '1px solid var(--border-subtle, rgba(255,255,255,0.06))'
                          : 'none',
                    }}
                  >
                    <span
                      className={`fk-badge--${s.passed ? 'completed' : 'failed'}`}
                      style={{ minWidth: 56 }}
                    >
                      {s.passed ? 'pass' : 'fail'}
                    </span>
                    <span style={{ minWidth: 180 }}>{s.entryName ?? '(unnamed)'}</span>
                    {!s.passed && s.reason ? (
                      <code
                        className="t-xs t-mono"
                        style={{ flex: 1, color: 'var(--text-secondary)' }}
                      >
                        {s.reason}
                      </code>
                    ) : (
                      <span style={{ flex: 1 }} />
                    )}
                    {s.runId ? (
                      <Link
                        to={routes.runDetail(s.runId)}
                        className="t-xs t-mono"
                        style={{ color: 'var(--horizon-glow)' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        full run trace →
                      </Link>
                    ) : null}
                  </div>
                ))}
              </div>
            </td>
          </tr>
        ) : null}
      </tbody>
    </table>
  );
}

// ─── Authoring form + entry row (ported from the old workflow-hosted page)

function AddEntryForm({
  workflowId,
  nodeKey,
  outputSchema,
  onCancel,
  onCreated,
}: {
  workflowId: string;
  nodeKey: string;
  outputSchema: unknown | null;
  onCancel: () => void;
  onCreated: () => void;
}) {
  const client = useClient();
  const skeleton = useMemo(() => skeletonFromSchema(outputSchema), [outputSchema]);
  const [name, setName] = useState('');
  const [input, setInput] = useState('{\n  \n}');
  const [expected, setExpected] = useState(JSON.stringify(skeleton, null, 2));
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      let parsedInput: unknown;
      let parsedExpected: unknown;
      try {
        parsedInput = JSON.parse(input);
      } catch (e) {
        throw new Error(`Input JSON: ${(e as Error).message}`);
      }
      try {
        parsedExpected = JSON.parse(expected);
      } catch (e) {
        throw new Error(`Expected JSON: ${(e as Error).message}`);
      }
      return client.http
        .post('v1/ground-truth', {
          json: {
            workflowId,
            nodeId: nodeKey,
            input: parsedInput,
            expectedOutput: parsedExpected,
            name: name.trim() || undefined,
          },
        })
        .json<GroundTruth>();
    },
    onSuccess: () => {
      setError(null);
      onCreated();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <form
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2)',
        padding: 'var(--sp-2)',
        background: 'var(--surface-1)',
        borderRadius: 4,
        marginBottom: 'var(--sp-2)',
      }}
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      <label className="field">
        <span className="field-label t-xs">Name (optional)</span>
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. tag-classifier-happy-path"
        />
      </label>
      <label className="field">
        <span className="field-label t-xs">Workflow input</span>
        <textarea
          className="textarea t-mono"
          rows={5}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          style={{ fontSize: 12 }}
        />
      </label>
      <label className="field">
        <span className="field-label t-xs">
          Expected output (this node only)
          <InfoHint text="Compared against the node's actual output with deep-equality at eval time." />
        </span>
        <textarea
          className="textarea t-mono"
          rows={8}
          value={expected}
          onChange={(e) => setExpected(e.target.value)}
          style={{ fontSize: 12 }}
        />
      </label>
      {error ? (
        <div className="alert alert--danger t-xs" style={{ whiteSpace: 'pre-wrap' }}>
          {error}
        </div>
      ) : null}
      <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn--primary btn--sm" disabled={create.isPending}>
          {create.isPending ? 'Saving…' : 'Save entry'}
        </button>
      </div>
    </form>
  );
}

function EntryRow({ entry, onDeleted }: { entry: GroundTruth; onDeleted: () => void }) {
  const client = useClient();
  const [open, setOpen] = useState(false);
  const del = useMutation({
    mutationFn: () => client.http.delete(`v1/ground-truth/${entry.id}`).json(),
    onSuccess: onDeleted,
  });

  return (
    <div
      style={{
        border: '1px solid var(--border-subtle)',
        borderRadius: 4,
        padding: 'var(--sp-2)',
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <div>
          <strong className="t-sm">{entry.name ?? '(unnamed)'}</strong>{' '}
          <code className="t-xs t-muted">{entry.id.slice(0, 10)}</code>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          <span className="t-xs t-muted">{formatDateTime(entry.createdAt)}</span>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={(e) => {
              e.stopPropagation();
              if (confirm(`Delete entry "${entry.name ?? entry.id.slice(0, 10)}"?`)) del.mutate();
            }}
            disabled={del.isPending}
          >
            {del.isPending ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </header>
      {open ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 'var(--sp-2)',
            marginTop: 'var(--sp-2)',
          }}
        >
          <div>
            <div className="t-xs t-muted" style={{ marginBottom: 4 }}>
              Input
            </div>
            <JsonViewer value={entry.input} />
          </div>
          <div>
            <div className="t-xs t-muted" style={{ marginBottom: 4 }}>
              Expected output
            </div>
            <JsonViewer value={entry.expectedOutput} />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function skeletonFromSchema(schema: unknown): unknown {
  if (!schema || typeof schema !== 'object') return {};
  const s = schema as Record<string, unknown>;
  if (s.type === 'object' && s.properties && typeof s.properties === 'object') {
    const props = s.properties as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const [k, p] of Object.entries(props)) out[k] = skeletonFromSchema(p);
    return out;
  }
  if (s.type === 'array') return [];
  if (s.type === 'string') return '';
  if (s.type === 'number' || s.type === 'integer') return 0;
  if (s.type === 'boolean') return false;
  return null;
}
